/**
 * bbk-v3 vaultData 编码（对齐 BBrokerFactory.vaultDataSchema / VaultConfig）
 */
import { encodeAbiParameters, parseUnits } from "viem";
import { BBROKER_STOCK_CATALOG } from "./bbrokerStockCatalog.js";

export const BBROKER_FACTORY_56 =
  "0xe524D478a55F2159bD81145c24BDDC93Dbe89def";
/** VaultPortal（发币），非交易 Portal */
export const VAULT_PORTAL_56 =
  "0x90497450f2a706f1951b5bdda52B4E5d16f34C06";
export const FLAP_CASTORE_URL = "https://flap.sh/bnb/CAstore";

export const ZERO = "0x0000000000000000000000000000000000000000";
export const MAX_LEGS = 10;

const VAULT_DATA_COMPONENTS = [
  { type: "string", name: "nftName" },
  { type: "string", name: "nftSymbol" },
  { type: "uint256", name: "maxNFTSupply" },
  { type: "uint256", name: "initialFloorNFTs" },
  { type: "uint256", name: "tokenCostPerNFT" },
  { type: "uint256", name: "dividendSharePercent" },
  { type: "uint256", name: "floorSharePercent" },
  { type: "string", name: "artSource" },
  { type: "address", name: "asset0" },
  { type: "address", name: "asset1" },
  { type: "address", name: "asset2" },
  { type: "address", name: "asset3" },
  { type: "address", name: "asset4" },
  { type: "address", name: "asset5" },
  { type: "address", name: "asset6" },
  { type: "address", name: "asset7" },
  { type: "address", name: "asset8" },
  { type: "address", name: "asset9" },
];

/** Portal isArray=false → 单 tuple */
const VAULT_DATA_TUPLE = {
  type: "tuple",
  components: VAULT_DATA_COMPONENTS,
};

export const BBROKER_DEFAULTS = {
  nftName: "币股NFT",
  nftSymbol: "BBK",
  maxNFTSupply: "5000",
  initialFloorNFTs: "5000",
  tokenCostPerNFTHuman: "100000",
  dividendSharePercent: 50,
  floorSharePercent: 50,
  artMode: "default",
  artSchema: "",
  artUrl: "",
  selected: [],
};

export function isAddr(v) {
  return typeof v === "string" && /^0x[a-fA-F0-9]{40}$/.test(v);
}

export function resolveArtSource(v) {
  // 链上成功发射用零地址字符串；空串可能被工厂判无效
  if (v.artMode === "default") {
    return "0x0000000000000000000000000000000000000000";
  }
  if (v.artMode === "schema") return String(v.artSchema || "").trim();
  return String(v.artUrl || "").trim();
}

export function padAssets10(selected) {
  const out = [];
  for (let i = 0; i < MAX_LEGS; i += 1) {
    const a = selected[i];
    out.push(a && isAddr(a) ? a : ZERO);
  }
  return out;
}

/**
 * @param {typeof BBROKER_DEFAULTS} v
 * @returns {{ hex: string, error?: string, assets10?: string[] }}
 */
export function encodeBBrokerVaultData(v) {
  const max = Number(v.maxNFTSupply);
  const floor = Number(v.initialFloorNFTs);
  const div = Math.round(Number(v.dividendSharePercent));
  const floorPct = Math.round(Number(v.floorSharePercent));

  if (!String(v.nftName || "").trim() || !String(v.nftSymbol || "").trim()) {
    return { hex: "0x", error: "币股NFT名称与符号必填" };
  }
  if (!Number.isInteger(max) || max < 1) {
    return { hex: "0x", error: "最大供应量无效" };
  }
  if (!Number.isInteger(floor) || floor < 1 || floor > max) {
    return { hex: "0x", error: "托底数量须在 1～最大供应量" };
  }
  if (!v.selected?.length || v.selected.length > MAX_LEGS) {
    return { hex: "0x", error: `请选择 1–${MAX_LEGS} 个 NFT 分红资产` };
  }
  if (div < 0 || div > 100 || floorPct < 0 || floorPct > 100) {
    return { hex: "0x", error: "持有奖励/地板池比例须在 0–100" };
  }
  if (div + floorPct !== 100) {
    return { hex: "0x", error: "持有奖励% + 地板池% 必须等于 100" };
  }

  let cost;
  try {
    cost = parseUnits(String(v.tokenCostPerNFTHuman || "0").trim() || "0", 18);
  } catch {
    return { hex: "0x", error: "销毁数量格式无效" };
  }
  if (cost <= 0n) return { hex: "0x", error: "每 NFT 销毁量须 > 0" };

  const art = resolveArtSource(v);
  if (v.artMode === "schema" && !isAddr(art)) {
    return { hex: "0x", error: "Schema 须为合约地址" };
  }
  if (v.artMode === "url" && !art) {
    return { hex: "0x", error: "请填写 metadata URL" };
  }

  const assets10 = padAssets10(v.selected);

  try {
    const [a0, a1, a2, a3, a4, a5, a6, a7, a8, a9] = assets10;
    const hex = encodeAbiParameters([VAULT_DATA_TUPLE], [
      {
        nftName: String(v.nftName).trim(),
        nftSymbol: String(v.nftSymbol).trim(),
        maxNFTSupply: BigInt(max),
        initialFloorNFTs: BigInt(floor),
        tokenCostPerNFT: cost,
        dividendSharePercent: BigInt(div),
        floorSharePercent: BigInt(floorPct),
        artSource: art,
        asset0: a0,
        asset1: a1,
        asset2: a2,
        asset3: a3,
        asset4: a4,
        asset5: a5,
        asset6: a6,
        asset7: a7,
        asset8: a8,
        asset9: a9,
      },
    ]);
    return { hex, assets10 };
  } catch (e) {
    return { hex: "0x", error: e instanceof Error ? e.message : "编码失败" };
  }
}

export function stockByAddress(addr) {
  const k = String(addr || "").toLowerCase();
  return BBROKER_STOCK_CATALOG.find((a) => a.address.toLowerCase() === k);
}

export { BBROKER_STOCK_CATALOG };
